import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  ABTest,
  Box,
  Container,
  FeatureGrid,
  HeroCarousel,
  LogoSection,
  LucidHead,
  PreFooterCta,
  Quote,
  BackNForth,
  TabCarousel,
} from '@lucid/components';
import { getI18nData, isDefined } from '@lucid/utils';

import ABTestNewHeroLayout from 'components/ab-tests/hero-layout/hero-layout';
import type { Lucid } from 'types/types';

// TODO Directus - get these defaults added to Directus or hard coded in some way.
// const backgroundAccents = [
//   {
//     desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/circle-background-accent.png',
//     mobile:
//       'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/circle-mobile-background-accent.png',
//   },
//   {
//     desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/hexagon-background-accent.png',
//     mobile:
//       'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/hexagon-mobile-background-accent.png',
//   },
//   {
//     desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/square-background-accent.png',
//     mobile:
//       'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/square-mobile-background-accent.png',
//   },
// ];

const ChartHomepage: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { shortCode, urlLangPrefix, localeId, pageType, nid, shouldHideSsr } = pageContext;
  const { directus, i18nData } = data;
  const node = directus.node;
  const { nodeTranslations } = node;
  const i18n = getI18nData(i18nData, shortCode);
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Chart_Homepage_Translations;
  const {
    hero_heading,
    hero_text,
    hero_link,
    carousel_items,
    quote,
    quote_link,
    logo_banner_section,
    back_n_forth_items,
    feature_grid_heading,
    feature_grid_text,
    feature_grid_items,
    pre_footer_heading,
    pre_footer_link,
    bottom_carousel_section,
  } = translatedData;

  const carouselItems = carousel_items?.map(ci => ci.carousel_item_id) as Lucid.Directus.Generated.Carousel_Item[];
  const bottomCarouselItems = bottom_carousel_section?.items?.map(cs => cs?.carousel_item_id).filter(isDefined);
  const featureGridItems = feature_grid_items?.map(fgi => fgi.feature_grid_item_id);
  const logoSection = logo_banner_section?.logo_section;
  const logoList = logoSection?.logo_list
    ?.filter(logo => logo.external_logo_id?.name ?? false)
    .map(logo => logo.external_logo_id!.name!);
  const backNForthItems = back_n_forth_items?.map(bnf => bnf.back_n_forth_id);
  const quoteData = [quote].filter(isDefined);

  return (
    <Layout
      localeId={localeId}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <ABTest
        isAboveTheFold
        branchesToRender={['T_A', 'T_B', 'T_C']}
        testName="FY25Q1_ChartBrandedNoCarousel"
        willTriggerFire={node.is_ppc && shortCode === 'en'}
      >
        <ABTest.Context.Consumer>
          {({ branch }) => (
            <>
              {branch !== 'T_A' && <ABTestNewHeroLayout cta={hero_link} i18nData={i18nData} />}
              {branch !== 'T_C' && (
                <HeroCarousel
                  hasLowerHeader
                  ariaLabel={i18n('mktg_template_aria_label_slide_title')}
                  cta={hero_link}
                  hasSSO={node.is_ppc}
                  // TODO: FY25Q1_ChartBrandedNoCarousel - restore hero_heading
                  header={branch !== 'T_B' ? hero_heading : undefined}
                  heroBackgroundColor="orange.5"
                  i18nData={data.i18nData}
                  items={carouselItems}
                  sliderTime={9000}
                  subheader={hero_text}
                />
              )}
            </>
          )}
        </ABTest.Context.Consumer>
      </ABTest>
      {logo_banner_section && (
        <Box
          aria-label={logo_banner_section.heading}
          as="section"
          css={theme => ({
            backgroundColor: theme.colors.coolGrey[5],
            paddingTop: theme.space.s40,
            paddingBottom: theme.space.s40,
            marginBottom: theme.space.s80,
          })}
          data-ab="logo-section_container"
        >
          <Container
            css={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <LogoSection
              css={{
                maxWidth: '1100px',
              }}
              height={logo_banner_section?.logo_section?.row_height}
              logos={logoList}
              logosColor="dark"
              width={150}
            />
          </Container>
        </Box>
      )}
      {backNForthItems && (
        <Container>
          <BackNForth
            isReversed
            data-ab="back-n-forth-section_container"
            sections={(backNForthItems || []).filter(isDefined)}
          />
        </Container>
      )}
      {quote?.translations?.[0] && (
        <Box data-ab="quote-section_container" mb={{ default: 9, tablet: 0 }}>
          <Quote cta={quote_link} quotes={quoteData} />
        </Box>
      )}
      <Container>
        {featureGridItems?.length && (
          <Box mb={8} mt={8}>
            <FeatureGrid
              data-ab="feature-grid-section_container"
              items={(featureGridItems || []).filter(isDefined)}
              linkProps={{
                variant: 'secondary',
              }}
              sectionSubtitle={feature_grid_text}
              sectionTitle={feature_grid_heading}
              variant="alternating"
            />
          </Box>
        )}
      </Container>
      {bottom_carousel_section && <TabCarousel {...bottom_carousel_section} items={bottomCarouselItems || []} />}
      {pre_footer_heading && pre_footer_link && (
        <PreFooterCta
          backgroundColor="coolGrey.80"
          ctas={[pre_footer_link]}
          data-ab="pre-footer-cta-section_container"
          heading={pre_footer_heading}
        />
      )}
    </Layout>
  );
};

export default ChartHomepage;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query ChartHomepageQuery($nid: ID!, $shortCode: String!, $localeId: String!) {
    directus {
      node: chart_homepage_by_id(id: $nid) {
        nodeTranslations: translations {
          is_published
          languages_code {
            short_code
          }
          path {
            url
          }
        }
        is_ppc
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          hero_heading
          hero_text
          hero_link {
            beacon_id
            default_link_text
            final_url
          }
          carousel_items {
            carousel_item_id {
              heading
              text
              media {
                filename_disk
                type
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt
                }
              }
              link {
                beacon_id
                default_link_text
                final_url
              }
            }
          }
          logo_banner_section {
            heading
            logo_section {
              row_height
              logo_layout_variant
              logo_color
              logo_list {
                order
                external_logo_id {
                  name
                }
              }
            }
          }
          back_n_forth_items {
            back_n_forth_id {
              id
              heading
              html
              image {
                filename_disk
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt
                }
              }
              logo {
                filename_disk
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt
                }
              }
              background_image {
                filename_disk
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt
                }
              }
              link {
                beacon_id
                final_url
                default_link_text
              }
            }
          }
          quote {
            full_name
            company
            company_logo {
              filename_disk
              translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                alt
              }
            }
            image {
              filename_disk
              translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                alt
              }
            }
            translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
              text
              position
            }
          }
          quote_link {
            beacon_id
            final_url
            default_link_text
          }
          feature_grid_heading
          feature_grid_text
          feature_grid_items {
            feature_grid_item_id {
              heading
              html
              image {
                filename_disk
                tags
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  alt
                }
              }
              link {
                beacon_id
                default_link_text
                final_url
              }
            }
          }
          bottom_carousel_section {
            heading
            text
            image {
              filename_disk
              tags
              translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                alt
              }
            }
            items {
              carousel_item_id {
                heading
                text
                media {
                  type
                  filename_disk
                  translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                    alt
                  }
                }
                link {
                  beacon_id
                  default_link_text
                  final_url
                }
              }
            }
            link {
              beacon_id
              default_link_text
              final_url
            }
          }
          pre_footer_heading
          pre_footer_link {
            beacon_id
            final_url
            default_link_text
          }
          meta_tags {
            title
            description
            canonical
            robots
            other
            image {
              filename_disk
            }
            schema_markup
          }
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$localeId, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_template_aria_label_slide_title
            mktg_ct_add_on_play_animation_label
            mktg_ct_add_on_pause_animation_label
            mktg_carousel_slide_previous
            mktg_carousel_slide_next
            mktg_competitor_pause_slideshow
            mktg_sso_or_continue_with
            mktg_ct_all_sign_up_free
            mktg_sso_sign_in
            mktg_sso_sign_in_product
          }
        }
      }
    }
  }
`;
